import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Button,
  Paper,
  TableSortLabel,
  Tabs,
  Tab
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useNavigate } from 'react-router-dom';
import { User } from '../../../types/user.types';
import { UtilityService } from '../../../types/utility.types';
import { getAllUsers } from '../../../api/users';
import api from '../../../utils/api';
import SubBrokerPerformance from '../../Admin/SubBrokerPerformance';

interface SubBrokerSummary {
  user: User;
  level: string;
  totalContracts: number;
  liveContracts: number;
  pendingContracts: number;
  totalCommissionValue: number;
  totalCommissionReceived: number;
  outstandingCommission: number;
}

type SortField = 'name' | 'level' | 'totalContracts' | 'liveContracts' | 'totalCommissionValue' | 'outstandingCommission';
type SortOrder = 'asc' | 'desc';

const SubBrokerOverview: React.FC = () => {
  const navigate = useNavigate();
  const [subBrokerSummaries, setSubBrokerSummaries] = useState<SubBrokerSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [sortField, setSortField] = useState<SortField>('name');
  const [sortOrder, setSortOrder] = useState<SortOrder>('asc');
  const [subTabValue, setSubTabValue] = useState(0);

  useEffect(() => {
    fetchSubBrokerData();
  }, []);

  const fetchSubBrokerData = async () => {
    try {
      setLoading(true);

      // Fetch all users and contracts in parallel
      const [users, contractsResponse] = await Promise.all([
        getAllUsers(),
        api.get('/utility-services')
      ]);

      const contracts: UtilityService[] = contractsResponse.data;

      // Filter sub-brokers (users with role 'user' and subBrokerLevel)
      const subBrokers = users.filter(
        (user: User) => user.role === 'user' && user.subBrokerLevel
      );

      // Calculate summary for each sub-broker
      const summaries: SubBrokerSummary[] = subBrokers.map((subBroker: User) => {
        // Filter contracts for this sub-broker
        const subBrokerContracts = contracts.filter(
          (contract: UtilityService) => 
            contract.subBroker && contract.subBroker.userId === subBroker._id
        );

        let totalCommissionValue = 0;
        let totalCommissionReceived = 0;
        let outstandingCommission = 0;
        let liveContracts = 0;
        let pendingContracts = 0;

        subBrokerContracts.forEach((contract: UtilityService) => {
          const contractValue = Number(contract.contractValue) || 0;
          const commissionRate = contract.subBroker?.commissionRate || 0;
          const myCommission = contractValue * (commissionRate / 100);
          
          totalCommissionValue += myCommission;

          // Calculate received commission (proportional to what's been paid)
          const totalReceived = Number(contract.totalCommissionReceived) || 0;
          const receivedPercentage = contractValue > 0 ? totalReceived / contractValue : 0;
          const myReceivedCommission = myCommission * receivedPercentage;
          
          totalCommissionReceived += myReceivedCommission;
          outstandingCommission += (myCommission - myReceivedCommission);

          // Count by status
          const status = contract.status?.toUpperCase();
          if (status === 'LIVE') {
            liveContracts++;
          } else if (status === 'PENDING') {
            pendingContracts++;
          }
        });

        return {
          user: subBroker,
          level: subBroker.subBrokerLevel || 'N/A',
          totalContracts: subBrokerContracts.length,
          liveContracts,
          pendingContracts,
          totalCommissionValue,
          totalCommissionReceived,
          outstandingCommission
        };
      });

      setSubBrokerSummaries(summaries);
    } catch (error) {
      console.error('Error fetching sub-broker data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  const sortedSummaries = [...subBrokerSummaries].sort((a, b) => {
    let aValue: any;
    let bValue: any;

    switch (sortField) {
      case 'name':
        aValue = `${a.user.firstName} ${a.user.lastName}`.toLowerCase();
        bValue = `${b.user.firstName} ${b.user.lastName}`.toLowerCase();
        break;
      case 'level':
        aValue = a.level;
        bValue = b.level;
        break;
      case 'totalContracts':
        aValue = a.totalContracts;
        bValue = b.totalContracts;
        break;
      case 'liveContracts':
        aValue = a.liveContracts;
        bValue = b.liveContracts;
        break;
      case 'totalCommissionValue':
        aValue = a.totalCommissionValue;
        bValue = b.totalCommissionValue;
        break;
      case 'outstandingCommission':
        aValue = a.outstandingCommission;
        bValue = b.outstandingCommission;
        break;
      default:
        return 0;
    }

    if (typeof aValue === 'string') {
      return sortOrder === 'asc' 
        ? aValue.localeCompare(bValue)
        : bValue.localeCompare(aValue);
    } else {
      return sortOrder === 'asc' ? aValue - bValue : bValue - aValue;
    }
  });

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP'
    }).format(value);
  };

  const handleViewDashboard = (subBrokerId: string) => {
    navigate(`/utilities/sub-broker-view/${subBrokerId}`);
  };

  const handleSubTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setSubTabValue(newValue);
  };

  if (loading && subTabValue === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={subTabValue} onChange={handleSubTabChange}>
          <Tab label="Overview" />
          <Tab label="Performance Report" />
        </Tabs>
      </Box>

      {subTabValue === 0 && (
        <Box>
          <Typography variant="h5" gutterBottom>
            Sub-Broker Overview
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            View and manage all sub-broker performance metrics
          </Typography>

          <Card sx={{
        backgroundColor: 'rgba(255, 255, 255, 0.05)',
        backdropFilter: 'blur(4px)',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        borderRadius: 2
      }}>
        <CardContent>
          <TableContainer component={Paper} sx={{ backgroundColor: 'transparent' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>
                    <TableSortLabel
                      active={sortField === 'name'}
                      direction={sortField === 'name' ? sortOrder : 'asc'}
                      onClick={() => handleSort('name')}
                    >
                      Sub-Broker Name
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortField === 'level'}
                      direction={sortField === 'level' ? sortOrder : 'asc'}
                      onClick={() => handleSort('level')}
                    >
                      Level
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right">
                    <TableSortLabel
                      active={sortField === 'totalContracts'}
                      direction={sortField === 'totalContracts' ? sortOrder : 'asc'}
                      onClick={() => handleSort('totalContracts')}
                    >
                      Total Contracts
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right">
                    <TableSortLabel
                      active={sortField === 'liveContracts'}
                      direction={sortField === 'liveContracts' ? sortOrder : 'asc'}
                      onClick={() => handleSort('liveContracts')}
                    >
                      Live Contracts
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right">Pending</TableCell>
                  <TableCell align="right">
                    <TableSortLabel
                      active={sortField === 'totalCommissionValue'}
                      direction={sortField === 'totalCommissionValue' ? sortOrder : 'asc'}
                      onClick={() => handleSort('totalCommissionValue')}
                    >
                      Total Commission
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right">Received</TableCell>
                  <TableCell align="right">
                    <TableSortLabel
                      active={sortField === 'outstandingCommission'}
                      direction={sortField === 'outstandingCommission' ? sortOrder : 'asc'}
                      onClick={() => handleSort('outstandingCommission')}
                    >
                      Outstanding
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedSummaries.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center">
                      <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                        No sub-brokers found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  sortedSummaries.map((summary) => (
                    <TableRow key={summary.user._id} hover>
                      <TableCell>
                        <Box>
                          <Typography variant="body2">
                            {summary.user.firstName} {summary.user.lastName}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {summary.user.email}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={summary.level}
                          size="small"
                          color="secondary"
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight="bold">
                          {summary.totalContracts}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Chip 
                          label={summary.liveContracts}
                          size="small"
                          color="success"
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Chip 
                          label={summary.pendingContracts}
                          size="small"
                          color="warning"
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight="bold">
                          {formatCurrency(summary.totalCommissionValue)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" color="success.main">
                          {formatCurrency(summary.totalCommissionReceived)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" color="warning.main">
                          {formatCurrency(summary.outstandingCommission)}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<VisibilityIcon />}
                          onClick={() => handleViewDashboard(summary.user._id)}
                        >
                          View Dashboard
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
        </Box>
      )}

      {subTabValue === 1 && (
        <SubBrokerPerformance />
      )}
    </Box>
  );
};

export default SubBrokerOverview;
